// A single RGBA texture holding everything the map samples: signed-distance
// fields for text glyphs, colour bitmaps for emoji, and the raster of every
// <image> the schemas reference.
//
// One texture means one draw call can cover a whole batch regardless of what it
// contains, which is the point — the SVG renderer's cost was per element, and
// splitting draws per texture would reintroduce that shape of problem.

const SHELF_PADDING = 1;

export class Atlas {
  constructor(gl) {
    this.gl = gl;

    const max = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    this.size = Math.min(4096, max);

    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      this.size,
      this.size,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      null
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    // Shelf packing: entries are placed left to right on a row whose height is
    // set by the first entry on it. Good enough for content that is added once
    // and never freed, and it keeps allocation to a couple of comparisons.
    this.shelfX = 0;
    this.shelfY = 0;
    this.shelfHeight = 0;
    this.full = false;
  }

  // Returns { x, y, u0, v0, u1, v1 } or null when the atlas is exhausted.
  allocate(width, height) {
    if (this.full) return null;

    const w = width + SHELF_PADDING;
    const h = height + SHELF_PADDING;

    if (w > this.size || h > this.size) return null;

    if (this.shelfX + w > this.size) {
      this.shelfY += this.shelfHeight;
      this.shelfX = 0;
      this.shelfHeight = 0;
    }

    if (this.shelfY + h > this.size) {
      this.full = true;
      console.warn(
        "[borg-map] texture atlas full; further content will not draw"
      );
      return null;
    }

    const x = this.shelfX;
    const y = this.shelfY;

    this.shelfX += w;
    if (h > this.shelfHeight) this.shelfHeight = h;

    return {
      x,
      y,
      u0: x / this.size,
      v0: y / this.size,
      u1: (x + width) / this.size,
      v1: (y + height) / this.size,
    };
  }

  // `source` is any TexImageSource already sized to the slot — in practice an
  // ImageData. Alpha is left unpremultiplied: distance fields carry their value
  // in the alpha channel, and the shader premultiplies on output.
  upload(slot, source) {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      slot.x,
      slot.y,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      source
    );
  }

  destroy() {
    if (this.texture) {
      this.gl.deleteTexture(this.texture);
      this.texture = null;
    }
  }
}
